import React, { useEffect, useMemo, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminListToolbar from '../../components/admin/AdminListToolbar';
import Pagination from '../../components/Pagination';
import { useSidebar } from '../../contexts/SidebarContext';
import { showSuccess, showError, showConfirm } from '../../utils/toast';

const AdminSubcategoriesContent = () => {
  const { isCollapsed } = useSidebar();
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', categoryId: '', active: true, featured: false });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('a-z');
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subcatsRes, catsRes] = await Promise.all([
        api.get('/subcategories'),
        api.get('/categories'),
      ]);
      setSubcategories(subcatsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSorted = useMemo(() => {
    let list = subcategories;
    const s = (search || '').toLowerCase().trim();
    if (s) {
      list = list.filter((sc) => {
        const catName = (sc.categoryId?.name || '').toLowerCase();
        return (sc.name || '').toLowerCase().includes(s) || (sc.slug || '').toLowerCase().includes(s) || catName.includes(s);
      });
    }
    list = [...list].sort((a, b) => {
      const na = (a.name || '').toLowerCase();
      const nb = (b.name || '').toLowerCase();
      return sort === 'z-a' ? nb.localeCompare(na) : na.localeCompare(nb);
    });
    return list;
  }, [subcategories, search, sort]);

  const totalItems = filteredAndSorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const paginatedList = useMemo(
    () => filteredAndSorted.slice((currentPage - 1) * limit, currentPage * limit),
    [filteredAndSorted, currentPage, limit]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubcategory) {
        await api.put(`/subcategories/${editingSubcategory._id}`, formData);
      } else {
        await api.post('/subcategories', formData);
      }
      setShowModal(false);
      setEditingSubcategory(null);
      setFormData({ name: '', categoryId: '', active: true, featured: false });
      fetchData();
      showSuccess(editingSubcategory ? 'Subcategory updated successfully!' : 'Subcategory created successfully!');
    } catch (error) {
      showError(error.response?.data?.message || 'Error saving subcategory');
    }
  };

  const handleEdit = (subcategory) => {
    setEditingSubcategory(subcategory);
    setFormData({
      name: subcategory.name,
      categoryId: subcategory.categoryId._id || subcategory.categoryId,
      active: subcategory.active,
      featured: subcategory.featured || false,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    showConfirm(
      'Are you sure you want to delete this subcategory? This action cannot be undone.',
      async () => {
        try {
          await api.delete(`/subcategories/${id}`);
          fetchData();
          showSuccess('Subcategory deleted successfully!');
        } catch (error) {
          showError(error.response?.data?.message || 'Error deleting subcategory');
        }
      }
    );
  };

  return (
    <>
      <SEO title="Manage Subcategories - Admin" description="Manage subcategories." noindex />

        <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
          <div className="container-custom py-6 md:py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-50">Manage Subcategories</h1>
          <button
            onClick={() => {
              setEditingSubcategory(null);
              setFormData({ name: '', categoryId: '', active: true, featured: false });
              setShowModal(true);
            }}
            className="btn-primary text-sm whitespace-nowrap"
          >
            <span className="hidden sm:inline">Add Subcategory</span>
            <span className="sm:hidden">+ Add</span>
          </button>
        </div>

        {!loading && (
          <AdminListToolbar
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            search={search}
            onSearch={() => { setSearch(searchInput); setCurrentPage(1); }}
            onClear={() => { setSearchInput(''); setSearch(''); setCurrentPage(1); }}
            sort={sort}
            onSortChange={(v) => { setSort(v); setCurrentPage(1); }}
            limit={limit}
            onLimitChange={(v) => { setLimit(v); setCurrentPage(1); }}
            searchPlaceholder="Search by name, slug, or category..."
          />
        )}

        {loading ? (
          <PageLoader message="Loading subcategories..." />
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-14">Sr. No.</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Featured</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                    {paginatedList.map((subcategory, index) => {
                      const srNo = (currentPage - 1) * limit + index + 1;
                      return (
                      <tr key={subcategory._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-400 text-sm">{srNo}</td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap font-semibold text-gray-900 dark:text-gray-50">{subcategory.name}</td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-700 dark:text-gray-300">{subcategory.categoryId?.name || 'N/A'}</td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <span className={`badge text-xs ${subcategory.active ? 'badge-success' : 'badge-error'}`}>
                            {subcategory.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <span className={`badge text-xs ${subcategory.featured ? 'badge-warning' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                            {subcategory.featured ? '⭐ Featured' : 'Not Featured'}
                          </span>
                        </td>
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleEdit(subcategory)}
                              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(subcategory._id)}
                              className="text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300 text-sm font-medium transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {paginatedList.map((subcategory, index) => {
                const srNo = (currentPage - 1) * limit + index + 1;
                return (
                <div key={subcategory._id} className="card p-4 md:p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{srNo}.</span>
                        <h3 className="font-bold text-lg text-gray-900 dark:text-gray-50">{subcategory.name}</h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{subcategory.categoryId?.name || 'N/A'}</p>
                      <div className="flex flex-wrap gap-2">
                        <span className={`badge text-xs ${subcategory.active ? 'badge-success' : 'badge-error'}`}>
                          {subcategory.active ? 'Active' : 'Inactive'}
                        </span>
                        <span className={`badge text-xs ${subcategory.featured ? 'badge-warning' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                          {subcategory.featured ? '⭐ Featured' : 'Not Featured'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handleEdit(subcategory)}
                      className="btn-outline text-sm py-1.5 px-3 flex-1"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(subcategory._id)}
                      className="btn-outline text-sm py-1.5 px-3 flex-1 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );})}
            </div>

            {paginatedList.length === 0 && (
              <div className="card p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-lg">{search ? 'No subcategories match your search.' : 'No subcategories found.'}</p>
              </div>
            )}

            {totalPages >= 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={limit}
                  itemsOnCurrentPage={paginatedList.length}
                  onPageChange={(p) => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                />
              </div>
            )}
          </>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 md:p-8 w-full max-w-md animate-scale-in">
              <h2 className="text-xl md:text-2xl font-bold mb-4 text-gray-800 dark:text-gray-50">
                {editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Category *</label>
                  <select
                    value={formData.categoryId}
                    onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                    required
                    className="input-field"
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label className="ml-2 text-gray-700 dark:text-gray-300">Active</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label className="ml-2 text-gray-700 dark:text-gray-300">⭐ Featured (Show on Home Page)</label>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="submit"
                    className="btn-primary flex-1"
                  >
                    {editingSubcategory ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingSubcategory(null);
                    }}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
          </div>
        </main>
    </>
  );
};

const AdminSubcategories = () => {
  return (
    <AdminLayout>
      <AdminSubcategoriesContent />
    </AdminLayout>
  );
};

export default AdminSubcategories;

